export interface AuthTransitionSuccess {
    redirectTo: string;
    resetAnalyticsIdentity: boolean;
}

export interface AuthTransitionFailure {
    error: string;
}

export type AuthTransitionResult =
    | AuthTransitionSuccess
    | AuthTransitionFailure;

export const SIGNUP_PROFILE_SETUP_ERROR =
    "Account created but profile setup failed. Please contact support.";

const AUTH_REDIRECT_BASE_URL = "https://auth-redirect.iloris.invalid";
const UNSAFE_URL_CHARACTER = /[\\\u0000-\u001F\u007F]/;

/**
 * Return a canonical root-relative redirect path, or null for untrusted input.
 *
 * Parsing against a fixed base catches browser URL semantics such as
 * protocol-relative and backslash-based external URLs. Decoding once for
 * validation also rejects malformed escapes and encoded versions of those
 * unsafe forms without changing the path returned to the browser.
 */
export function safeAuthRedirectPath(value: unknown): string | null {
    if (
        typeof value !== "string" ||
        !value.startsWith("/") ||
        value.startsWith("//") ||
        UNSAFE_URL_CHARACTER.test(value)
    ) {
        return null;
    }

    let decodedValue: string;
    try {
        decodedValue = decodeURIComponent(value);
    } catch {
        return null;
    }

    if (
        decodedValue.startsWith("//") ||
        UNSAFE_URL_CHARACTER.test(decodedValue)
    ) {
        return null;
    }

    try {
        const parsedUrl = new URL(value, AUTH_REDIRECT_BASE_URL);
        if (parsedUrl.origin !== AUTH_REDIRECT_BASE_URL) return null;

        const canonicalPath = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        if (
            !canonicalPath.startsWith("/") ||
            canonicalPath.startsWith("//") ||
            UNSAFE_URL_CHARACTER.test(canonicalPath)
        ) {
            return null;
        }

        return canonicalPath;
    } catch {
        return null;
    }
}

/**
 * Supabase reports the expected anonymous state as AuthSessionMissingError.
 * Any other lookup error leaves the prior browser identity ambiguous, so an
 * ensuing authenticated transition must conservatively separate analytics.
 */
export function isAuthIdentityLookupAmbiguous(error: unknown): boolean {
    if (!error) return false;
    return !(
        typeof error === "object" &&
        "name" in error &&
        error.name === "AuthSessionMissingError"
    );
}

/**
 * A browser analytics identity belongs to the existing authenticated account.
 * Reset it before replacing that account with a different authenticated user.
 */
export function shouldResetAnalyticsIdentity(
    previousUserId: string | null | undefined,
    nextUserId: string | null | undefined,
    previousIdentityUnknown = false,
): boolean {
    return Boolean(
        nextUserId &&
            (previousIdentityUnknown ||
                (previousUserId && previousUserId !== nextUserId)),
    );
}
